export type LegislationBlock = {
  blockId: string
  blockOrder: number
  rawText: string
}

export type HighlightBlockRange = {
  startBlockId: string
  endBlockId: string
}

const ARTICLE_NUMBER_GLOBAL_PATTERN = /\bArticle\s*(\d+[A-Za-z]?)\b/gi
const ARTICLE_PARAGRAPH_REF_PATTERN =
  /\bArticle\s*(\d+[A-Za-z]?)\s*\((\d+)\)/gi
const ARTICLE_PARAGRAPH_SUB_REF_PATTERN =
  /\bArticle\s*(\d+[A-Za-z]?)\s*\((\d+)\)\s*\(([a-z])\)/gi
const ARTICLE_PARAGRAPH_RANGE_PATTERN =
  /\bArticle\s*(\d+[A-Za-z]?)\s*\((\d+)\)\s*(?:to|–|-)\s*\((\d+)\)/gi
/** "Article 6(1) and 6(2)" — article number repeated without the word Article. */
const ARTICLE_PARAGRAPH_SHORTHAND_REF_PATTERN =
  /\band\s+(\d+[A-Za-z]?)\s*\((\d+)\)/gi
/** "Article 6(1) and (2)" — paragraph number only, same article as earlier in the label. */
const PARAGRAPH_ONLY_SHORTHAND_REF_PATTERN = /\band\s+\((\d+)\)/gi
const LETTERED_SUBPARAGRAPH_BLOCK_PATTERN = /^\(([a-z])\)(?:\s*\|)?/i
const MAJOR_DIVISION_HEADING_PATTERN =
  /^(Annex|Appendix|Chapter|Title|Part|Section)\b/i
const PARAGRAPH_NUMBER_DOT_PATTERN = /^(\d+)\./
const PARAGRAPH_NUMBER_PAREN_PATTERN = /^\((\d+)\)\s*/

export const CHUNK_SEARCH_PADDING_BLOCKS = 5
export const MIN_CHUNK_TEXT_OVERLAP_RATIO = 0.5

function parseArticleHeadingBlock(
  rawText: string
): { articleNumber: string } | null {
  const trimmed = rawText.trim()
  const match = trimmed.match(/^Article\s*(\d+[A-Za-z]?)\b/i)
  if (!match?.[1]) {
    return null
  }

  const afterNumber = trimmed.slice(match[0].length)
  if (afterNumber.length === 0) {
    return { articleNumber: match[1] }
  }
  if (/^\s*[([]/.test(afterNumber)) {
    return { articleNumber: match[1] }
  }
  if (/^\s+[A-Z([]/.test(afterNumber)) {
    return { articleNumber: match[1] }
  }
  return null
}

export function parseArticleNumbers(text: string | null | undefined): string[] {
  if (!text) {
    return []
  }
  const numbers: string[] = []
  const seen = new Set<string>()
  for (const match of text.matchAll(ARTICLE_NUMBER_GLOBAL_PATTERN)) {
    const articleNumber = match[1]
    if (!articleNumber) {
      continue
    }
    const key = articleNumber.toLowerCase()
    if (seen.has(key)) {
      continue
    }
    seen.add(key)
    numbers.push(articleNumber)
  }
  return numbers
}

export function parseArticleNumber(text: string | null | undefined): string | null {
  return parseArticleNumbers(text)[0] ?? null
}

export type ArticleParagraphRef = {
  articleNumber: string
  paragraphNumber: string
  subParagraphLabel?: string
}

function paragraphRefKey(ref: ArticleParagraphRef): string {
  const subPart = ref.subParagraphLabel
    ? `:${ref.subParagraphLabel.toLowerCase()}`
    : ''
  return `${ref.articleNumber.toLowerCase()}:${ref.paragraphNumber}${subPart}`
}

function paragraphRefsWithoutSubLabels(
  refs: ArticleParagraphRef[]
): ArticleParagraphRef[] {
  const seen = new Set<string>()
  const output: ArticleParagraphRef[] = []
  for (const ref of refs) {
    const key = `${ref.articleNumber.toLowerCase()}:${ref.paragraphNumber}`
    if (seen.has(key)) {
      continue
    }
    seen.add(key)
    output.push({
      articleNumber: ref.articleNumber,
      paragraphNumber: ref.paragraphNumber,
    })
  }
  return output
}

export function parseArticleParagraphRefs(
  text: string | null | undefined
): ArticleParagraphRef[] {
  if (!text) {
    return []
  }

  const refs: ArticleParagraphRef[] = []
  const seen = new Set<string>()
  const consumedRangeSpans: Array<{ start: number; end: number }> = []

  for (const match of text.matchAll(ARTICLE_PARAGRAPH_RANGE_PATTERN)) {
    const articleNumber = match[1]
    const fromParagraph = match[2]
    const toParagraph = match[3]
    if (!articleNumber || !fromParagraph || !toParagraph) {
      continue
    }
    const start = Number.parseInt(fromParagraph, 10)
    const end = Number.parseInt(toParagraph, 10)
    if (!Number.isFinite(start) || !Number.isFinite(end)) {
      continue
    }
    consumedRangeSpans.push({
      start: match.index ?? 0,
      end: (match.index ?? 0) + match[0].length,
    })
    const [rangeStart, rangeEnd] =
      start <= end ? [start, end] : [end, start]
    for (let paragraph = rangeStart; paragraph <= rangeEnd; paragraph += 1) {
      const paragraphNumber = String(paragraph)
      const ref = { articleNumber, paragraphNumber }
      const key = paragraphRefKey(ref)
      if (seen.has(key)) {
        continue
      }
      seen.add(key)
      refs.push(ref)
    }
  }

  for (const match of text.matchAll(ARTICLE_PARAGRAPH_SUB_REF_PATTERN)) {
    const articleNumber = match[1]
    const paragraphNumber = match[2]
    const subParagraphLabel = match[3]
    if (!articleNumber || !paragraphNumber || !subParagraphLabel) {
      continue
    }
    const ref = {
      articleNumber,
      paragraphNumber,
      subParagraphLabel: subParagraphLabel.toLowerCase(),
    }
    const key = paragraphRefKey(ref)
    if (seen.has(key)) {
      continue
    }
    seen.add(key)
    consumedRangeSpans.push({
      start: match.index ?? 0,
      end: (match.index ?? 0) + match[0].length,
    })
    refs.push(ref)
  }

  for (const match of text.matchAll(ARTICLE_PARAGRAPH_REF_PATTERN)) {
    const matchStart = match.index ?? 0
    if (
      consumedRangeSpans.some(
        (span) => matchStart >= span.start && matchStart < span.end
      )
    ) {
      continue
    }
    const articleNumber = match[1]
    const paragraphNumber = match[2]
    if (!articleNumber || !paragraphNumber) {
      continue
    }
    const ref = { articleNumber, paragraphNumber }
    const key = paragraphRefKey(ref)
    if (seen.has(key)) {
      continue
    }
    seen.add(key)
    refs.push(ref)
  }

  for (const match of text.matchAll(ARTICLE_PARAGRAPH_SHORTHAND_REF_PATTERN)) {
    const articleNumber = match[1]
    const paragraphNumber = match[2]
    if (!articleNumber || !paragraphNumber) {
      continue
    }
    const ref = { articleNumber, paragraphNumber }
    const key = paragraphRefKey(ref)
    if (seen.has(key)) {
      continue
    }
    seen.add(key)
    refs.push(ref)
  }

  const primaryArticleNumber =
    refs[refs.length - 1]?.articleNumber ?? parseArticleNumber(text)
  if (primaryArticleNumber) {
    for (const match of text.matchAll(PARAGRAPH_ONLY_SHORTHAND_REF_PATTERN)) {
      const paragraphNumber = match[1]
      if (!paragraphNumber) {
        continue
      }
      const ref = { articleNumber: primaryArticleNumber, paragraphNumber }
      const key = paragraphRefKey(ref)
      if (seen.has(key)) {
        continue
      }
      seen.add(key)
      refs.push(ref)
    }
  }

  return refs
}

export function parseLetteredSubparagraphFromBlock(
  rawText: string
): string | null {
  const match = rawText.trim().match(LETTERED_SUBPARAGRAPH_BLOCK_PATTERN)
  return match?.[1]?.toLowerCase() ?? null
}

function isLetteredSubparagraphBlock(rawText: string): boolean {
  return parseLetteredSubparagraphFromBlock(rawText) != null
}

/** After a `(a)` marker block, body text often continues in following blocks without a letter prefix. */
function resolveLetteredSubparagraphEndIndex(input: {
  blocks: LegislationBlock[]
  markerIndex: number
  spanEndIndex: number
}): number {
  let endIndex = input.markerIndex

  for (let nextIndex = input.markerIndex + 1; nextIndex <= input.spanEndIndex; nextIndex += 1) {
    const nextBlock = input.blocks[nextIndex]
    if (!nextBlock) {
      continue
    }
    if (
      isAnyArticleHeading(nextBlock.rawText) ||
      isMajorDivisionHeading(nextBlock.rawText)
    ) {
      break
    }
    if (isLetteredSubparagraphBlock(nextBlock.rawText)) {
      break
    }
    if (parseParagraphNumberFromBlock(nextBlock.rawText)) {
      break
    }
    endIndex = nextIndex
  }

  return endIndex
}

export function parseParagraphNumberFromBlock(rawText: string): string | null {
  const trimmed = rawText.trim()
  const dotMatch = trimmed.match(PARAGRAPH_NUMBER_DOT_PATTERN)
  if (dotMatch?.[1]) {
    return dotMatch[1]
  }
  const parenMatch = trimmed.match(PARAGRAPH_NUMBER_PAREN_PATTERN)
  return parenMatch?.[1] ?? null
}

function isLikelyArticleSubtitleBlock(rawText: string): boolean {
  const trimmed = rawText.trim()
  if (
    trimmed.length === 0 ||
    parseParagraphNumberFromBlock(trimmed) ||
    isLetteredSubparagraphBlock(trimmed)
  ) {
    return false
  }
  if (isAnyArticleHeading(trimmed) || isMajorDivisionHeading(trimmed)) {
    return false
  }
  if (trimmed.length > 120 || /[;:]/.test(trimmed)) {
    return false
  }
  return !/\.\s/.test(trimmed)
}

export type ArticleParagraphSpan = {
  paragraphNumber: string
  startIndex: number
  endIndex: number
}

export function listArticleParagraphSpans(input: {
  blocks: LegislationBlock[]
  articleScopeStartIndex: number
  articleScopeEndIndex: number
}): ArticleParagraphSpan[] {
  const { blocks, articleScopeStartIndex, articleScopeEndIndex } = input
  const spans: ArticleParagraphSpan[] = []
  let ordinal = 0
  let index = articleScopeStartIndex + 1

  while (index <= articleScopeEndIndex) {
    const block = blocks[index]
    if (!block) {
      index += 1
      continue
    }
    if (
      isAnyArticleHeading(block.rawText) ||
      isMajorDivisionHeading(block.rawText)
    ) {
      index += 1
      continue
    }
    if (isLikelyArticleSubtitleBlock(block.rawText)) {
      index += 1
      continue
    }

    const currentParagraphNumber = parseParagraphNumberFromBlock(block.rawText)
    const paragraphNumber = currentParagraphNumber ?? String(++ordinal)
    let endIndex = index

    for (let nextIndex = index + 1; nextIndex <= articleScopeEndIndex; nextIndex += 1) {
      const nextBlock = blocks[nextIndex]
      if (!nextBlock) {
        continue
      }
      if (
        isAnyArticleHeading(nextBlock.rawText) ||
        isMajorDivisionHeading(nextBlock.rawText)
      ) {
        endIndex = nextIndex - 1
        break
      }
      if (isLetteredSubparagraphBlock(nextBlock.rawText)) {
        endIndex = nextIndex
        continue
      }
      const nextParagraphNumber = parseParagraphNumberFromBlock(nextBlock.rawText)
      if (nextParagraphNumber) {
        endIndex = nextIndex - 1
        break
      }
      if (currentParagraphNumber) {
        continue
      }
      if (!isLikelyArticleSubtitleBlock(nextBlock.rawText)) {
        endIndex = nextIndex
      }
    }

    spans.push({
      paragraphNumber,
      startIndex: index,
      endIndex,
    })
    index = endIndex + 1
  }

  return spans
}

function resolveParagraphBlockRangeInArticle(input: {
  blocks: LegislationBlock[]
  articleScopeStartIndex: number
  articleScopeEndIndex: number
  paragraphNumber: string
  subParagraphLabel?: string
}): HighlightBlockRange | null {
  const {
    blocks,
    articleScopeStartIndex,
    articleScopeEndIndex,
    paragraphNumber,
    subParagraphLabel,
  } = input

  const span = listArticleParagraphSpans({
    blocks,
    articleScopeStartIndex,
    articleScopeEndIndex,
  }).find(
    (candidate) => candidate.paragraphNumber === paragraphNumber
  )
  if (!span) {
    return null
  }

  let startIndex = span.startIndex
  let endIndex = span.endIndex

  if (subParagraphLabel) {
    const normalizedLabel = subParagraphLabel.toLowerCase()
    let subparagraphEndIndex = -1

    for (let index = span.startIndex; index <= span.endIndex; index += 1) {
      const block = blocks[index]
      if (!block) {
        continue
      }
      const letter = parseLetteredSubparagraphFromBlock(block.rawText)
      if (letter !== normalizedLabel) {
        continue
      }
      subparagraphEndIndex = index
      break
    }

    if (subparagraphEndIndex < 0) {
      // Sub-paragraph marker not found in blocks — highlight the full numbered paragraph.
    } else {
      endIndex = resolveLetteredSubparagraphEndIndex({
        blocks,
        markerIndex: subparagraphEndIndex,
        spanEndIndex: span.endIndex,
      })
      startIndex = span.startIndex
    }
  }

  const startBlock = blocks[startIndex]
  const endBlock = blocks[endIndex]
  if (!startBlock || !endBlock) {
    return null
  }

  return {
    startBlockId: startBlock.blockId,
    endBlockId: endBlock.blockId,
  }
}

export function labelCitesArticleParagraphs(
  articleLabel: string | null | undefined
): boolean {
  if (!articleLabel) {
    return false
  }
  return /\(\d+\)/.test(articleLabel) || /\([a-z]\)/i.test(articleLabel)
}

export function resolveArticleParagraphsBlockRange(input: {
  blocks: LegislationBlock[]
  paragraphRefs: ArticleParagraphRef[]
}): HighlightBlockRange | null {
  if (input.paragraphRefs.length === 0) {
    return null
  }

  const blocks = sortBlocksByOrder(input.blocks)
  const indexById = blockIndexById(blocks)

  let minIndex = Number.POSITIVE_INFINITY
  let maxIndex = Number.NEGATIVE_INFINITY
  let foundAny = false

  for (const ref of input.paragraphRefs) {
    const articleRange = resolveArticleBlockRange({
      blocks,
      articleNumber: ref.articleNumber,
    })
    if (!articleRange) {
      continue
    }
    const articleStartIndex = indexById.get(articleRange.startBlockId)
    const articleEndIndex = indexById.get(articleRange.endBlockId)
    if (articleStartIndex === undefined || articleEndIndex === undefined) {
      continue
    }

    const paragraphRange = resolveParagraphBlockRangeInArticle({
      blocks,
      articleScopeStartIndex: articleStartIndex,
      articleScopeEndIndex: articleEndIndex,
      paragraphNumber: ref.paragraphNumber,
      subParagraphLabel: ref.subParagraphLabel,
    })
    if (!paragraphRange) {
      continue
    }
    const startIndex = indexById.get(paragraphRange.startBlockId)
    const endIndex = indexById.get(paragraphRange.endBlockId)
    if (startIndex === undefined || endIndex === undefined) {
      continue
    }
    foundAny = true
    minIndex = Math.min(minIndex, startIndex, endIndex)
    maxIndex = Math.max(maxIndex, startIndex, endIndex)
  }

  if (!foundAny || !Number.isFinite(minIndex) || !Number.isFinite(maxIndex)) {
    return null
  }

  const startBlock = blocks[minIndex]
  const endBlock = blocks[maxIndex]
  if (!startBlock || !endBlock) {
    return null
  }

  return {
    startBlockId: startBlock.blockId,
    endBlockId: endBlock.blockId,
  }
}

export function intersectHighlightBlockRanges(input: {
  blocks: LegislationBlock[]
  left: HighlightBlockRange
  right: HighlightBlockRange
}): HighlightBlockRange | null {
  const blocks = sortBlocksByOrder(input.blocks)
  const indexById = blockIndexById(blocks)
  const leftStart = indexById.get(input.left.startBlockId)
  const leftEnd = indexById.get(input.left.endBlockId)
  const rightStart = indexById.get(input.right.startBlockId)
  const rightEnd = indexById.get(input.right.endBlockId)
  if (
    leftStart === undefined ||
    leftEnd === undefined ||
    rightStart === undefined ||
    rightEnd === undefined
  ) {
    return null
  }
  const minIndex = Math.max(
    Math.min(leftStart, leftEnd),
    Math.min(rightStart, rightEnd)
  )
  const maxIndex = Math.min(
    Math.max(leftStart, leftEnd),
    Math.max(rightStart, rightEnd)
  )
  if (minIndex > maxIndex) {
    return null
  }
  const startBlock = blocks[minIndex]
  const endBlock = blocks[maxIndex]
  if (!startBlock || !endBlock) {
    return null
  }
  return {
    startBlockId: startBlock.blockId,
    endBlockId: endBlock.blockId,
  }
}

function resolveArticleScopeRangeForParagraphRefs(input: {
  blocks: LegislationBlock[]
  paragraphRefs: ArticleParagraphRef[]
}): HighlightBlockRange | null {
  const blocks = sortBlocksByOrder(input.blocks)
  const indexById = blockIndexById(blocks)
  let minIndex = Number.POSITIVE_INFINITY
  let maxIndex = Number.NEGATIVE_INFINITY
  let foundAny = false

  const articleNumbers = [
    ...new Set(
      input.paragraphRefs.map((ref) => ref.articleNumber.toLowerCase())
    ),
  ]

  for (const articleKey of articleNumbers) {
    const articleNumber =
      input.paragraphRefs.find(
        (ref) => ref.articleNumber.toLowerCase() === articleKey
      )?.articleNumber ?? articleKey
    const articleRange = resolveArticleBlockRange({
      blocks,
      articleNumber,
    })
    if (!articleRange) {
      continue
    }
    const startIndex = indexById.get(articleRange.startBlockId)
    const endIndex = indexById.get(articleRange.endBlockId)
    if (startIndex === undefined || endIndex === undefined) {
      continue
    }
    foundAny = true
    minIndex = Math.min(minIndex, startIndex, endIndex)
    maxIndex = Math.max(maxIndex, startIndex, endIndex)
  }

  if (!foundAny || !Number.isFinite(minIndex) || !Number.isFinite(maxIndex)) {
    return null
  }

  const startBlock = blocks[minIndex]
  const endBlock = blocks[maxIndex]
  if (!startBlock || !endBlock) {
    return null
  }

  return {
    startBlockId: startBlock.blockId,
    endBlockId: endBlock.blockId,
  }
}

function resolveLocatorBlockRange(input: {
  blocks: LegislationBlock[]
  startBlockId: string
  endBlockId: string
}): HighlightBlockRange | null {
  const blocks = sortBlocksByOrder(input.blocks)
  const indexById = blockIndexById(blocks)
  const startIndex = indexById.get(input.startBlockId)
  const endIndex = indexById.get(input.endBlockId)
  if (startIndex === undefined || endIndex === undefined) {
    return null
  }
  const minIndex = Math.min(startIndex, endIndex)
  const maxIndex = Math.max(startIndex, endIndex)
  const startBlock = blocks[minIndex]
  const endBlock = blocks[maxIndex]
  if (!startBlock || !endBlock) {
    return null
  }
  return {
    startBlockId: startBlock.blockId,
    endBlockId: endBlock.blockId,
  }
}

function resolveLocatorRangeClampedToParagraphRefs(input: {
  blocks: LegislationBlock[]
  paragraphRefs: ArticleParagraphRef[]
  startBlockId?: string | null
  endBlockId?: string | null
}): HighlightBlockRange | null {
  if (!input.startBlockId || !input.endBlockId) {
    return null
  }
  const locatorRange = resolveLocatorBlockRange({
    blocks: input.blocks,
    startBlockId: input.startBlockId,
    endBlockId: input.endBlockId,
  })
  if (!locatorRange) {
    return null
  }
  const articleScopeRange = resolveArticleScopeRangeForParagraphRefs({
    blocks: input.blocks,
    paragraphRefs: input.paragraphRefs,
  })
  if (!articleScopeRange) {
    return locatorRange
  }
  return (
    intersectHighlightBlockRanges({
      blocks: input.blocks,
      left: locatorRange,
      right: articleScopeRange,
    }) ?? articleScopeRange
  )
}

export function isArticleHeadingForNumber(
  rawText: string,
  articleNumber: string
): boolean {
  const heading = parseArticleHeadingBlock(rawText)
  if (!heading) {
    return false
  }
  return heading.articleNumber.toLowerCase() === articleNumber.toLowerCase()
}

function isAnyArticleHeading(rawText: string): boolean {
  return parseArticleHeadingBlock(rawText) != null
}

function isMajorDivisionHeading(rawText: string): boolean {
  return MAJOR_DIVISION_HEADING_PATTERN.test(rawText.trim())
}

function sortBlocksByOrder(blocks: LegislationBlock[]): LegislationBlock[] {
  return [...blocks].sort((left, right) => left.blockOrder - right.blockOrder)
}

function blockIndexById(
  blocks: LegislationBlock[]
): Map<string, number> {
  const indexById = new Map<string, number>()
  blocks.forEach((block, index) => {
    indexById.set(block.blockId, index)
  })
  return indexById
}

export function resolveArticleBlockRange(input: {
  blocks: LegislationBlock[]
  articleNumber: string
}): HighlightBlockRange | null {
  const blocks = sortBlocksByOrder(input.blocks)
  const startIndex = blocks.findIndex((block) =>
    isArticleHeadingForNumber(block.rawText, input.articleNumber)
  )
  if (startIndex < 0) {
    return null
  }

  let endIndex = blocks.length - 1
  for (let index = startIndex + 1; index < blocks.length; index += 1) {
    const block = blocks[index]
    if (!block) {
      continue
    }
    if (isAnyArticleHeading(block.rawText) || isMajorDivisionHeading(block.rawText)) {
      endIndex = index - 1
      break
    }
  }

  if (endIndex < startIndex) {
    return null
  }

  const startBlock = blocks[startIndex]
  const endBlock = blocks[endIndex]
  if (!startBlock || !endBlock) {
    return null
  }

  return {
    startBlockId: startBlock.blockId,
    endBlockId: endBlock.blockId,
  }
}

export function resolveArticlesBlockRange(input: {
  blocks: LegislationBlock[]
  articleNumbers: string[]
}): HighlightBlockRange | null {
  if (input.articleNumbers.length === 0) {
    return null
  }

  const blocks = sortBlocksByOrder(input.blocks)
  const indexById = blockIndexById(blocks)
  let minIndex = Number.POSITIVE_INFINITY
  let maxIndex = Number.NEGATIVE_INFINITY
  let foundAny = false

  for (const articleNumber of input.articleNumbers) {
    const articleRange = resolveArticleBlockRange({
      blocks,
      articleNumber,
    })
    if (!articleRange) {
      continue
    }
    const startIndex = indexById.get(articleRange.startBlockId)
    const endIndex = indexById.get(articleRange.endBlockId)
    if (startIndex === undefined || endIndex === undefined) {
      continue
    }
    foundAny = true
    minIndex = Math.min(minIndex, startIndex, endIndex)
    maxIndex = Math.max(maxIndex, startIndex, endIndex)
  }

  if (!foundAny || !Number.isFinite(minIndex) || !Number.isFinite(maxIndex)) {
    return null
  }

  const startBlock = blocks[minIndex]
  const endBlock = blocks[maxIndex]
  if (!startBlock || !endBlock) {
    return null
  }

  return {
    startBlockId: startBlock.blockId,
    endBlockId: endBlock.blockId,
  }
}

export function normalizeTextForMatch(text: string): string {
  return text
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
}

function joinBlockText(blocks: LegislationBlock[]): string {
  return blocks.map((block) => block.rawText).join('\n\n')
}

export function scoreChunkTextOverlap(
  blockText: string,
  chunkText: string
): number {
  const normalizedBlocks = normalizeTextForMatch(blockText)
  const normalizedChunk = normalizeTextForMatch(chunkText)
  if (!normalizedBlocks || !normalizedChunk) {
    return 0
  }
  if (normalizedBlocks === normalizedChunk) {
    return 1
  }
  if (normalizedChunk.includes(normalizedBlocks)) {
    return normalizedBlocks.length / normalizedChunk.length
  }
  if (normalizedBlocks.includes(normalizedChunk)) {
    return normalizedChunk.length / normalizedBlocks.length
  }

  const chunkWords = normalizedChunk.split(' ').filter((word) => word.length > 3)
  if (chunkWords.length === 0) {
    return 0
  }
  const matchedWords = chunkWords.filter((word) =>
    normalizedBlocks.includes(word)
  ).length
  return matchedWords / chunkWords.length
}

function findBestContiguousOverlap(input: {
  blocks: LegislationBlock[]
  chunkText: string
  scopeStartIndex: number
  scopeEndIndex: number
}): HighlightBlockRange | null {
  const { blocks, chunkText, scopeStartIndex, scopeEndIndex } = input
  let bestRange: HighlightBlockRange | null = null
  let bestScore = 0

  for (let startIndex = scopeStartIndex; startIndex <= scopeEndIndex; startIndex += 1) {
    for (let endIndex = startIndex; endIndex <= scopeEndIndex; endIndex += 1) {
      const slice = blocks.slice(startIndex, endIndex + 1)
      const score = scoreChunkTextOverlap(joinBlockText(slice), chunkText)
      if (score > bestScore) {
        bestScore = score
        const startBlock = slice[0]
        const endBlock = slice[slice.length - 1]
        if (startBlock && endBlock) {
          bestRange = {
            startBlockId: startBlock.blockId,
            endBlockId: endBlock.blockId,
          }
        }
      }
    }
  }

  if (!bestRange || bestScore < MIN_CHUNK_TEXT_OVERLAP_RATIO) {
    return null
  }
  return bestRange
}

function resolveChunkTextBlockRangeInScope(input: {
  blocks: LegislationBlock[]
  chunkText: string
  scopeStartIndex: number
  scopeEndIndex: number
}): HighlightBlockRange | null {
  if (input.scopeStartIndex > input.scopeEndIndex) {
    return null
  }
  return findBestContiguousOverlap(input)
}

export function resolveChunkTextBlockRange(input: {
  blocks: LegislationBlock[]
  chunkText: string
  startBlockId: string
  endBlockId: string
  paddingBlocks?: number
}): HighlightBlockRange | null {
  const blocks = sortBlocksByOrder(input.blocks)
  if (blocks.length === 0) {
    return null
  }

  const indexById = blockIndexById(blocks)
  const chunkStartIndex = indexById.get(input.startBlockId)
  const chunkEndIndex = indexById.get(input.endBlockId)
  const padding = input.paddingBlocks ?? CHUNK_SEARCH_PADDING_BLOCKS

  if (chunkStartIndex !== undefined && chunkEndIndex !== undefined) {
    const windowStart = Math.max(
      0,
      Math.min(chunkStartIndex, chunkEndIndex) - padding
    )
    const windowEnd = Math.min(
      blocks.length - 1,
      Math.max(chunkStartIndex, chunkEndIndex) + padding
    )
    const windowMatch = resolveChunkTextBlockRangeInScope({
      blocks,
      chunkText: input.chunkText,
      scopeStartIndex: windowStart,
      scopeEndIndex: windowEnd,
    })
    if (windowMatch) {
      return windowMatch
    }
  }

  return resolveChunkTextBlockRangeInScope({
    blocks,
    chunkText: input.chunkText,
    scopeStartIndex: 0,
    scopeEndIndex: blocks.length - 1,
  })
}

export function resolveCitationHighlightRange(input: {
  blocks: LegislationBlock[]
  articleLabel?: string | null
  chunkText?: string | null
  startBlockId?: string | null
  endBlockId?: string | null
}): HighlightBlockRange | null {
  const labelParagraphRefs = parseArticleParagraphRefs(input.articleLabel)
  const labelHasParagraphCitations = labelCitesArticleParagraphs(input.articleLabel)
  if (labelParagraphRefs.length > 0) {
    let paragraphRange = resolveArticleParagraphsBlockRange({
      blocks: input.blocks,
      paragraphRefs: labelParagraphRefs,
    })
    if (!paragraphRange) {
      const refsWithoutSubLabels = paragraphRefsWithoutSubLabels(labelParagraphRefs)
      if (refsWithoutSubLabels.length > 0) {
        paragraphRange = resolveArticleParagraphsBlockRange({
          blocks: input.blocks,
          paragraphRefs: refsWithoutSubLabels,
        })
      }
    }
    if (paragraphRange) {
      return paragraphRange
    }

    const clampedLocatorRange = resolveLocatorRangeClampedToParagraphRefs({
      blocks: input.blocks,
      paragraphRefs: labelParagraphRefs,
      startBlockId: input.startBlockId,
      endBlockId: input.endBlockId,
    })
    if (clampedLocatorRange) {
      return clampedLocatorRange
    }

    const articleScopeRange = resolveArticleScopeRangeForParagraphRefs({
      blocks: input.blocks,
      paragraphRefs: labelParagraphRefs,
    })
    const chunkText = input.chunkText?.trim()
    if (chunkText && articleScopeRange && input.startBlockId && input.endBlockId) {
      const chunkRange = resolveChunkTextBlockRange({
        blocks: input.blocks,
        chunkText,
        startBlockId: articleScopeRange.startBlockId,
        endBlockId: articleScopeRange.endBlockId,
      })
      if (chunkRange) {
        return chunkRange
      }
    }
    if (articleScopeRange) {
      return articleScopeRange
    }
  }

  const labelArticleNumbers = labelHasParagraphCitations
    ? []
    : parseArticleNumbers(input.articleLabel)
  const articleNumbers =
    labelArticleNumbers.length > 0
      ? labelArticleNumbers
      : labelHasParagraphCitations
        ? []
        : parseArticleNumbers(input.chunkText)

  if (articleNumbers.length > 0) {
    const articleRange =
      articleNumbers.length === 1
        ? resolveArticleBlockRange({
            blocks: input.blocks,
            articleNumber: articleNumbers[0]!,
          })
        : resolveArticlesBlockRange({
            blocks: input.blocks,
            articleNumbers,
          })
    if (articleRange) {
      return articleRange
    }
  }

  if (input.startBlockId && input.endBlockId) {
    const locatorRange = resolveLocatorBlockRange({
      blocks: input.blocks,
      startBlockId: input.startBlockId,
      endBlockId: input.endBlockId,
    })
    if (locatorRange) {
      return locatorRange
    }
  }

  const chunkText = input.chunkText?.trim()
  if (chunkText && input.startBlockId && input.endBlockId) {
    return resolveChunkTextBlockRange({
      blocks: input.blocks,
      chunkText,
      startBlockId: input.startBlockId,
      endBlockId: input.endBlockId,
    })
  }

  return null
}
